import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Car, Check, History, Loader2, MoonStar, Pencil, Route, X } from 'lucide-react';
import api from '../../utils/api';
import { Dialog, TableSkeleton, ErrorState, money, num } from '../../components/ui';
import { Notice } from '../Financials/financeKit';
import { useConfirm } from '../../components/ConfirmDialog';
import { CategoryChip, categoryLabel, dmy, errText } from '../Settings/payRatesKit';

// One timesheet's pay lines on a company paid from Pay Rates (owner spec 6.3 to 6.6): one line per
// category per day, the sleepover unit, km. Also where an admin decides a late sign-out after a
// sleepover, and where the public holiday state warning shows. Amounts are the server's; the
// total is only those amounts added up.
//
// The Km panel (spec 6.5): driving the client (the worker's GPS trips) and travel between shifts
// (map km from the previous shift that day, when Pay travel is ticked). Before approval an admin
// can change either figure; a reason is required and every change is listed in the log.

const LINE_STATUS = {
  calculated: { label: 'Calculated', cls: 'bg-slate-100 text-slate-700 ring-slate-200' },
  sent:       { label: 'Sent',       cls: 'bg-blue-50 text-blue-700 ring-blue-200' },
  processed:  { label: 'Processed in Xero', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-200' },
  adjusted:   { label: 'Adjusted',   cls: 'bg-amber-50 text-amber-800 ring-amber-200' },
};

const minutesText = (m) => {
  const n = Math.round(Number(m) || 0);
  const h = Math.floor(n / 60);
  const r = n % 60;
  if (!h) return `${r} min`;
  return r ? `${h} h ${r} min` : `${h} h`;
};

const unitsText = (line) => {
  const u = Number(line.units) || 0;
  if (line.category === 'sleepover') return u === 1 ? '1 sleepover' : `${num(u, 0)} sleepovers`;
  if (line.category === 'km') return `${num(u, 2)} km`;
  return `${num(u, 2)} h`;
};
const rateUnit = (c) => (c === 'sleepover' ? 'shift' : c === 'km' ? 'km' : 'hr');
const KM_SOURCE = { trip: 'Driving the client', travel: 'Travel between shifts' };
const kmText = (v) => `${num(Number(v) || 0, 2)} km`;
// '2026-10-29 14:05:00' -> '29/10/2026, 2:05 pm' (the server's wall-clock time, never shifted).
function whenText(v) {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(String(v || ''));
  if (!m) return dmy(v);
  const h = Number(m[4]);
  return `${m[3]}/${m[2]}/${m[1]}, ${h % 12 || 12}:${m[5]} ${h < 12 ? 'am' : 'pm'}`;
}

// Change one km figure: the new km and a reason (both required). Saves through PUT .../km.
function KmEdit({ timesheetId, kind, current, onSaved, onCancel }) {
  const [km, setKm] = useState(String(Number(current) || 0));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const kmId = `km-${kind}-value`; const reasonId = `km-${kind}-reason`;
  const save = async (e) => {
    e.preventDefault();
    const n = Number(km);
    if (String(km).trim() === '' || !Number.isFinite(n) || n < 0) { setError('Enter the km as a number of 0 or more.'); return; }
    if (reason.trim().length < 3) { setError('Give a reason for changing the km.'); return; }
    setSaving(true); setError('');
    try {
      const { data } = await api.put(`/pay-rates/timesheets/${timesheetId}/km`, { kind, km: n, reason: reason.trim() });
      toast.success('Km changed and logged');
      onSaved(data);
    } catch (err) { setError(errText(err, "That wasn't saved. Try again.")); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={save} className="mt-2.5 space-y-2.5 rounded-lg border border-slate-200 bg-white p-3">
      <div className="grid gap-2.5 sm:grid-cols-[9rem_1fr]">
        <div>
          <label htmlFor={kmId} className="block text-[12px] font-medium text-slate-600">New km</label>
          <input id={kmId} type="number" min="0" step="0.01" inputMode="decimal" className="input-field mt-1" value={km}
            onChange={(e) => { setKm(e.target.value); setError(''); }} />
        </div>
        <div>
          <label htmlFor={reasonId} className="block text-[12px] font-medium text-slate-600">Reason (required)</label>
          <input id={reasonId} type="text" maxLength={500} className="input-field mt-1" value={reason} placeholder="For example: drop-off pin was left on at home"
            onChange={(e) => { setReason(e.target.value); setError(''); }} />
        </div>
      </div>
      {error && <p className="text-[13px] text-rose-700" role="alert">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className="btn-primary btn-sm" disabled={saving}>
          {saving ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Check size={13} aria-hidden="true" />}Save km
        </button>
        <button type="button" className="btn-secondary btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
      </div>
    </form>
  );
}

function KmRow({ icon: Icon, title, km, detail, children, action }) {
  return (
    <div className="flex items-start gap-3 px-4 py-3">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"><Icon size={16} aria-hidden="true" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <p className="text-sm font-semibold text-slate-900">{title}</p>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold tabular-nums text-slate-900">{kmText(km)}</span>
            {action}
          </div>
        </div>
        {detail && <p className="mt-0.5 text-[13px] leading-relaxed text-slate-600">{detail}</p>}
        {children}
      </div>
    </div>
  );
}

// The Km panel: both kinds of km, the change form and the change log.
function KmPanel({ timesheetId, km, onChanged }) {
  const [editing, setEditing] = useState('');
  if (!km) return null;
  const { trip = {}, travel = {}, adjustments = [] } = km;
  const changeBtn = (kind) => (km.editable && editing !== kind ? (
    <button type="button" className="btn-secondary btn-sm" onClick={() => setEditing(kind)} aria-label={`Change ${KM_SOURCE[kind].toLowerCase()} km`}>
      <Pencil size={12} aria-hidden="true" />Change
    </button>
  ) : null);
  const saved = (data) => { setEditing(''); onChanged(data); };

  const tripDetail = trip.company_vehicle ? 'Company vehicle: these km are not paid to the worker.'
    : trip.held ? 'The approver held these km back, so they are not paid.'
    : [trip.adjusted ? 'Entered by an admin (see the log below).' : 'From the worker\'s Start trip and End trip.',
      trip.auto_km > 0 && travel.km > 0 && !trip.adjusted ? `${kmText(trip.auto_km)} of auto travel legs are left out, because Pay travel pays that journey by map.` : null]
      .filter(Boolean).join(' ');
  let travelDetail;
  if (!travel.ticked) travelDetail = 'Pay travel is not ticked on this shift. Tick it on the Schedule Board to pay travel from the previous shift that day.';
  else if (travel.adjusted) travelDetail = 'Entered by an admin (see the log below).';
  else if (travel.from && travel.to) travelDetail = `By map from ${travel.from}${travel.prev_start ? ` (the ${travel.prev_start} shift)` : ''} to ${travel.to}.`;
  else travelDetail = null;

  return (
    <section aria-label="Kilometres" className="rounded-xl border border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50/60 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-slate-900">Kilometres</h3>
        <span className="text-[13px] tabular-nums text-slate-600">Paid at the worker's Km rate: <span className="font-semibold text-slate-900">{kmText(km.total_km)}</span></span>
      </div>
      <div className="divide-y divide-slate-100">
        <KmRow icon={Car} title={KM_SOURCE.trip} km={trip.km} detail={tripDetail} action={changeBtn('trip')}>
          {editing === 'trip' && <KmEdit timesheetId={timesheetId} kind="trip" current={trip.recorded_km} onSaved={saved} onCancel={() => setEditing('')} />}
        </KmRow>
        <KmRow icon={Route} title={KM_SOURCE.travel} km={travel.km} detail={travelDetail} action={travel.ticked ? changeBtn('travel') : null}>
          {editing === 'travel' && <KmEdit timesheetId={timesheetId} kind="travel" current={travel.km} onSaved={saved} onCancel={() => setEditing('')} />}
        </KmRow>
      </div>
      {!km.editable && km.locked_reason && (
        <p className="border-t border-slate-100 px-4 py-2.5 text-[13px] text-slate-600">{km.locked_reason}</p>
      )}
      {adjustments.length > 0 && (
        <div className="border-t border-slate-200">
          <p className="flex items-center gap-1.5 px-4 pt-2.5 text-[12px] font-semibold text-slate-500"><History size={13} aria-hidden="true" />Km change log</p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="text-[12px] font-semibold text-slate-500">
                  <th scope="col" className="px-4 py-1.5 text-left">When</th>
                  <th scope="col" className="px-3 py-1.5 text-left">Km</th>
                  <th scope="col" className="px-3 py-1.5 text-right">Change</th>
                  <th scope="col" className="px-3 py-1.5 text-left">Reason</th>
                  <th scope="col" className="px-4 py-1.5 text-left">By</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a) => (
                  <tr key={a.id} className="border-t border-slate-100 align-top">
                    <td className="px-4 py-2 tabular-nums text-slate-700 whitespace-nowrap">{whenText(a.created_at)}</td>
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{KM_SOURCE[a.kind] || a.kind}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-900 whitespace-nowrap">{kmText(a.original_km)} to {kmText(a.new_km)}</td>
                    <td className="px-3 py-2 text-slate-700">{a.reason}</td>
                    <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{a.user_name || 'Unknown user'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

// Warning codes the pay engine stores on a timesheet, in words. The late sign-out code is left out
// because the box below already says it.
const WARNING_TEXT = {
  no_client_state: { title: 'Public holidays not checked', text: "The client's address has no state, so the hours were classed without public holidays. Add the state to the client's address and the lines are recalculated." },
  ph_year_not_covered: { title: 'Public holidays not checked', text: "Rostery has no public holiday calendar for this date in the client's state, so the hours were classed without public holidays." },
  no_hours: { title: 'No hours to pay', text: 'This timesheet has no approved or rostered times to pay.' },
  lines_sent: { title: 'Already sent', text: 'These pay lines have been sent, so they are not recalculated. A change becomes an adjustment.' },
  pay_travel_no_earlier_shift: { title: 'No travel km paid', text: 'Pay travel is ticked on this shift, but the worker has no earlier shift that day to travel from. Travel is paid between shifts on the same day only.' },
  travel_address_missing: { title: 'No travel km paid', text: "Rostery couldn't place one of the two addresses on the map, so the travel between shifts was not measured. Check the shift address, the house address or the client's address." },
  travel_km_unavailable: { title: 'Travel km not measured yet', text: "The map service didn't answer, so no travel km is paid for now. It is measured again the next time this timesheet is recalculated, or you can enter the km with a reason." },
  travel_no_route: { title: 'No travel km paid', text: 'The map found no driving route between the two shift addresses. Check the addresses, or enter the km with a reason.' },
};
function warningOf(w) {
  const code = typeof w === 'string' ? w : (w && (w.code || w.message || w.text)) || '';
  if (!code || code === 'late_signout_awaiting_approval') return null;
  if (WARNING_TEXT[code]) return { key: code, ...WARNING_TEXT[code] };
  if (code.startsWith('missing_rate')) {
    const cat = code.split(':')[1];
    const which = cat ? `${categoryLabel(cat)} rate` : 'rate';
    return { key: code, title: 'Rate missing', text: `This worker has no ${which} on this date, so that time is paid at $0.00. Set it on their profile under Pay Rates.` };
  }
  return { key: code, title: 'Check this timesheet', text: code.includes(' ') ? code : code.replace(/_/g, ' ') };
}

// The API may answer with the lines alone or with the lines and their context.
function normalise(data) {
  if (Array.isArray(data)) return { lines: data, warnings: [] };
  const d = data || {};
  const lines = d.lines || d.pay_lines || [];
  const raw = [].concat(d.warnings || [], d.warning || [], (d.km && d.km.warnings) || []);
  const seen = new Set();
  const warnings = raw.map(warningOf).filter((w) => w && !seen.has(w.key) && seen.add(w.key));
  const out = { ...d, lines: Array.isArray(lines) ? lines : [], warnings };
  if ('pending_extra' in d) {
    out.pending_extra_minutes = d.pending_extra ? Number(d.pending_extra.minutes) || 0 : 0;
    out.pending_extra_status = d.pending_extra ? d.pending_extra.status : null;
  }
  return out;
}

export const extraMinutesOf = (ts) => Number(ts && ts.pending_extra_minutes) || 0;
export const extraStatusOf = (ts) => (ts && (ts.pending_extra_status || ts.extra_status)) || null;

export default function PayLinesDialog({ ts, open, onClose, onChanged }) {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [loadErr, setLoadErr] = useState('');
  const [deciding, setDeciding] = useState('');

  const id = ts && ts.id;
  const load = useCallback(() => {
    if (!id) return;
    setData(null);
    setLoadErr('');
    api.get(`/pay-rates/timesheets/${id}/pay-lines`)
      .then((r) => setData(normalise(r.data)))
      .catch((e) => setLoadErr(errText(e, "We couldn't load the pay lines. Try again.")));
  }, [id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const extra = data && data.pending_extra_minutes !== undefined && data.pending_extra_minutes !== null
    ? Number(data.pending_extra_minutes) || 0
    : extraMinutesOf(ts);
  const extraStatus = extraStatusOf(data) || extraStatusOf(ts) || 'awaiting_approval';
  const extraOpen = extra > 0 && !['approved', 'rejected'].includes(extraStatus);

  const decide = async (action) => {
    if (action === 'reject') {
      const ok = await confirm(`The extra ${minutesText(extra)} will not be paid. The sleepover itself stays paid as 1 unit.`,
        { title: 'Reject the late sign-out time?', confirmLabel: 'Reject', danger: true });
      if (!ok) return;
    }
    setDeciding(action);
    try {
      try {
        await api.post(`/pay-rates/timesheets/${id}/extra/${action}`);
      } catch (e) {
        if (e.response?.status !== 404 || e.response?.data?.message) throw e;
        await api.post(`/timesheets/${id}/extra/${action}`);
      }
      toast.success(action === 'approve' ? `${minutesText(extra)} approved and added as hourly time` : 'Late sign-out time rejected');
      load();
      if (onChanged) onChanged();
    } catch (e) { toast.error(errText(e, "That wasn't saved. Try again.")); }
    finally { setDeciding(''); }
  };

  const lines = (data && data.lines) || [];
  const total = data && data.total !== undefined && data.total !== null
    ? Number(data.total)
    : lines.reduce((s, l) => s + Math.round((Number(l.amount) || 0) * 100), 0) / 100;
  // Km is paid on top of the hours and sleepovers (the server keeps them apart, as pay_amount does).
  const kmTotal = data && data.km_total != null ? Number(data.km_total) : 0;
  const grandTotal = data && data.grand_total != null ? Number(data.grand_total) : total;
  const who = (ts && (ts.staff_name || ts.name)) || 'This worker';
  const when = ts && ts.shift_date ? dmy(String(ts.shift_date).slice(0, 10)) : '';

  return (
    <Dialog open={open} onClose={onClose} size="lg"
      title="Pay lines"
      description={[who, when, ts && ts.client_names].filter(Boolean).join(', ')}
      footer={<button type="button" className="btn-secondary" onClick={onClose}>Close</button>}>
      <div className="space-y-4">
        {data && data.engine === 'award' && (
          <Notice tone="slate">This company pays from SCHADS pay groups, so this timesheet has no pay lines.</Notice>
        )}

        {data && data.warnings && data.warnings.map((w) => (
          <Notice key={w.key} tone="amber" title={w.title}>{w.text}</Notice>
        ))}

        {extra > 0 && (
          <div className={clsx('rounded-xl border px-4 py-3', extraOpen ? 'border-violet-200 bg-violet-50' : 'border-slate-200 bg-slate-50')}>
            <div className="flex items-start gap-3">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white text-violet-600 ring-1 ring-violet-200"><MoonStar size={16} aria-hidden="true" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-900">Late sign-out after a sleepover</p>
                <p className="mt-0.5 text-[13px] leading-relaxed text-slate-700">
                  {who} signed out {minutesText(extra)} after the rostered end.
                  {extraOpen
                    ? ' Approve to pay that time as hourly time at the category that applies. The sleepover stays 1 unit either way.'
                    : extraStatus === 'approved' ? ' This time was approved and is paid as hourly time.' : ' This time was rejected and is not paid.'}
                </p>
                {extraOpen && (
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    <button type="button" className="btn-primary btn-sm" onClick={() => decide('approve')} disabled={!!deciding}>
                      {deciding === 'approve' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <Check size={13} aria-hidden="true" />}Approve
                    </button>
                    <button type="button" className="btn-secondary btn-sm" onClick={() => decide('reject')} disabled={!!deciding}>
                      {deciding === 'reject' ? <Loader2 size={13} className="animate-spin" aria-hidden="true" /> : <X size={13} aria-hidden="true" />}Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {loadErr ? (
          <div className="rounded-xl border border-slate-200"><ErrorState message={loadErr} onRetry={load} /></div>
        ) : !data ? (
          <div className="rounded-xl border border-slate-200"><TableSkeleton rows={3} cols={6} /></div>
        ) : lines.length === 0 ? (
          data.engine !== 'award' && (
            <p className="rounded-xl border border-slate-200 px-4 py-6 text-center text-sm text-slate-500">
              No pay lines yet. They are worked out when the timesheet is approved. You can check and change the km below first.
            </p>
          )
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[600px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/60 text-[12px] font-semibold text-slate-500">
                  <th scope="col" className="px-4 py-2 text-left">Category</th>
                  <th scope="col" className="px-3 py-2 text-left">Date</th>
                  <th scope="col" className="px-3 py-2 text-right">Units</th>
                  <th scope="col" className="px-3 py-2 text-right">Rate</th>
                  <th scope="col" className="px-3 py-2 text-right">Amount</th>
                  <th scope="col" className="px-4 py-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => {
                  const st = LINE_STATUS[l.status] || LINE_STATUS.calculated;
                  return (
                    <tr key={l.id || `${l.category}-${l.line_date || l.date}-${i}`} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2">
                        <CategoryChip category={l.category} />
                        {l.category === 'km' && KM_SOURCE[l.source] && <span className="ml-2 text-[12px] text-slate-500">{KM_SOURCE[l.source]}</span>}
                      </td>
                      <td className="px-3 py-2 tabular-nums text-slate-700 whitespace-nowrap">{dmy(l.line_date || l.date)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">{unitsText(l)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700 whitespace-nowrap">{money(l.rate)}<span className="text-[11.5px] text-slate-400">/{rateUnit(l.category)}</span></td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums text-slate-900 whitespace-nowrap">{money(l.amount)}</td>
                      <td className="px-4 py-2">
                        <span className={clsx('inline-flex items-center rounded-full px-2 py-0.5 text-[12px] font-medium ring-1 ring-inset whitespace-nowrap', st.cls)}>{st.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                {kmTotal > 0 && (
                  <>
                    <tr className="border-t border-slate-300">
                      <td colSpan={4} className="px-4 py-1.5 text-[13px] text-slate-600">Hours and sleepovers</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{money(total)}</td>
                      <td />
                    </tr>
                    <tr>
                      <td colSpan={4} className="px-4 py-1.5 text-[13px] text-slate-600">Km</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{money(kmTotal)}</td>
                      <td />
                    </tr>
                  </>
                )}
                <tr className="border-t border-slate-300 bg-slate-50/70">
                  <td colSpan={4} className="px-4 py-2 text-[13px] font-semibold text-slate-700">Total</td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums text-slate-900">{money(kmTotal > 0 ? grandTotal : total)}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {data && data.engine === 'rates' && data.km && (
          <KmPanel timesheetId={id} km={data.km} onChanged={(d) => { setData(normalise(d)); if (onChanged) onChanged(); }} />
        )}
      </div>
    </Dialog>
  );
}
